// src/hooks/useConsents.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// =====================================================
// QUERIES
// =====================================================

export const useConsents = (userId, options = {}) => {
  return useQuery({
    queryKey: ['consents', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    ...options
  });
};

export const useConsent = (userId, consentType, options = {}) => {
  return useQuery({
    queryKey: ['consents', userId, consentType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consents')
        .select('*')
        .eq('user_id', userId)
        .eq('consent_type', consentType)
        .is('revoked_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!consentType,
    ...options
  });
};

export const useHasPhotoConsent = (userId, options = {}) => {
  return useQuery({
    queryKey: ['consents', userId, 'photo_sharing_internal', 'status'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consents')
        .select('granted')
        .eq('user_id', userId)
        .eq('consent_type', 'photo_sharing_internal')
        .is('revoked_at', null)
        .eq('granted', true)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!userId,
    ...options
  });
};

/**
 * Check if user has given all required consents (for teachers/staff)
 * Required: terms_of_service, privacy_policy; branch_admin/super_admin also need data_processing (DPA)
 */
export const useRequiredConsents = (userId, options = {}) => {
  return useQuery({
    queryKey: ['consents', userId, 'required'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consents')
        .select('consent_type, granted')
        .eq('user_id', userId)
        .in('consent_type', ['terms_of_service', 'privacy_policy', 'data_processing'])
        .is('revoked_at', null)
        .eq('granted', true);

      if (error) throw error;

      const grantedTypes = (data || []).map(c => c.consent_type);

      return {
        hasTermsOfService: grantedTypes.includes('terms_of_service'),
        hasPrivacyPolicy: grantedTypes.includes('privacy_policy'),
        hasDataProcessing: grantedTypes.includes('data_processing'),
        missing: [
          !grantedTypes.includes('terms_of_service') && 'terms_of_service',
          !grantedTypes.includes('privacy_policy') && 'privacy_policy',
          !grantedTypes.includes('data_processing') && 'data_processing'
        ].filter(Boolean)
      };
    },
    enabled: !!userId,
    ...options
  });
};

/**
 * Grant multiple consents at once (for consent dialog)
 */
export const useGrantMultipleConsents = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, consentTypes, userAgent }) => {
      const consentsToInsert = consentTypes.map(consentType => ({
        user_id: userId,
        consent_type: consentType,
        granted: true,
        granted_at: new Date().toISOString(),
        user_agent: userAgent
      }));

      const { data, error } = await supabase
        .from('consents')
        .insert(consentsToInsert)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
    onError: (error) => {
      logger.error('Grant multiple consents error', { message: error.message });
      toast.error(`Nie udało się zapisać zgód: ${error.message}`);
    }
  });
};

// =====================================================
// MUTATIONS
// =====================================================

export const useGrantConsent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, consentType, ipAddress, userAgent }) => {
      const { data, error } = await supabase
        .from('consents')
        .insert({
          user_id: userId,
          consent_type: consentType,
          granted: true,
          granted_at: new Date().toISOString(),
          ip_address: ipAddress,
          user_agent: userAgent
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success('Zgoda została udzielona');
    },
    onError: (error) => {
      logger.error('Grant consent error', { message: error.message });
      toast.error(`Nie udało się zapisać zgody: ${error.message}`);
    }
  });
};

export const useRevokeConsent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (consentId) => {
      const { data, error } = await supabase
        .from('consents')
        .update({ 
          revoked_at: new Date().toISOString() 
        })
        .eq('id', consentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success('Zgoda została cofnięta');
    },
    onError: (error) => {
      logger.error('Revoke consent error', { message: error.message });
      toast.error(`Failed: ${error.message}`);
    }
  });
};

export const useToggleConsent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, consentType, granted, ipAddress, userAgent }) => {
      if (granted) {
        // Grant new consent
        const { data, error } = await supabase
          .from('consents')
          .insert({
            user_id: userId,
            consent_type: consentType,
            granted: true,
            granted_at: new Date().toISOString(),
            ip_address: ipAddress,
            user_agent: userAgent
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Revoke existing consent(s) — handle duplicate rows (same user + consent_type)
        const { data: rows, error: fetchError } = await supabase
          .from('consents')
          .select('id')
          .eq('user_id', userId)
          .eq('consent_type', consentType)
          .is('revoked_at', null);

        if (fetchError) throw fetchError;

        if (rows && rows.length > 0) {
          const ids = rows.map((r) => r.id);
          const { data: updated, error } = await supabase
            .from('consents')
            .update({ revoked_at: new Date().toISOString() })
            .in('id', ids)
            .select();

          if (error) throw error;
          return Array.isArray(updated) ? updated[0] : updated;
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['consents'] });
      queryClient.invalidateQueries({ queryKey: ['photos'] });
      toast.success(variables.granted ? 'Zgoda została udzielona' : 'Zgoda została cofnięta');
    },
    onError: (error) => {
      logger.error('Toggle consent error', { message: error.message });
      toast.error(`Failed: ${error.message}`);
    }
  });
};

// =====================================================
// PHOTO CONSENT HELPERS
// =====================================================

/**
 * Check if all parents of given children have photo consent.
 * Uses RPC check_children_photo_consent (SECURITY DEFINER) so teachers can see
 * parents' consent without RLS blocking the consents table.
 *
 * @param {string[]} childIds - Array of child IDs to check
 * @returns {Promise<{ allConsented: boolean, childrenWithoutConsent: Array<{childId: string, childName?: string, parentIds: string[]}>, consentStatus: Map<string, boolean> }>}
 */
export async function checkChildrenConsentStatus(childIds) {
  if (!childIds || childIds.length === 0) {
    return { allConsented: true, childrenWithoutConsent: [], consentStatus: new Map() };
  }

  const { data, error } = await supabase.rpc('check_children_photo_consent', {
    p_child_ids: childIds
  });

  if (error) throw error;

  const allConsented = data?.all_consented ?? true;
  const rawList = data?.children_without_consent ?? [];
  const childrenWithoutConsent = rawList.map((item) => ({
    childId: item.childId,
    childName: item.childName ?? '',
    parentIds: Array.isArray(item.parentIds) ? item.parentIds : []
  }));

  const withoutConsentIds = new Set(childrenWithoutConsent.map((c) => c.childId));
  const consentStatus = new Map(
    childIds.map((id) => [id, !withoutConsentIds.has(id)])
  );

  logger.debug('Consent check (RPC)', {
    allConsented,
    childrenWithoutConsentCount: childrenWithoutConsent.length,
    childIds
  });  return {
    allConsented,
    childrenWithoutConsent,
    consentStatus
  };
}

/**
 * React Query hook to check photo consent status for multiple children
 * @param {string[]} childIds - Array of child IDs
 * @param {object} options - React Query options
 */
export const useChildrenPhotoConsentStatus = (childIds = [], options = {}) => {
  return useQuery({
    queryKey: ['consents', 'children', childIds],
    queryFn: () => checkChildrenConsentStatus(childIds),
    enabled: childIds && childIds.length > 0,
    ...options
  });
};
